import { useState, BaseSyntheticEvent, useEffect, useCallback } from 'react';
import {
  mergeWithKey,
  mergeAll,
  pipe,
  toPairs,
  fromPairs,
  reject,
  isNil,
  map,
  assocPath,
  mapObjIndexed,
  uniq,
} from 'ramda';

export type TValidationResult<T> = {
  [Key in keyof T]: {
    error: string;
  };
};

interface IValidationRule {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  predicate: (value: unknown | any) => boolean;
  message: React.ReactNode;
}

export type TValidationRules<T> = {
  [Key in keyof T]: IValidationRule[];
};

export type TValidationErrors<T> = {
  [Key in keyof T]: string[];
};

interface IValidationObject {
  rules: IValidationRule[];
  value: unknown;
}

const makeValidationObject = pipe(
  mergeWithKey((_, rules, value) => ({
    rules,
    value,
  })),
  toPairs
);

const runValidation = map((val: [string, IValidationObject]) => {
  const [key, { rules, value }] = val;

  const runRules = (rules: IValidationRule[]) =>
    map(({ predicate, message }) => (predicate(value) ? null : message), rules);

  const results = pipe(runRules, reject(isNil))(rules);

  return { [key]: results };
});

const runValidationRules = <T>(
  rules: TValidationRules<T>,
  values: T,
  touched: string[]
): TValidationErrors<T> => {
  // Only validate if field has been touched
  const touchedRules = fromPairs(
    toPairs(rules).map(([field, rule]) => {
      if (touched.includes(field)) return [field, rule];
      return [field, []];
    })
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validationObject: any = makeValidationObject(touchedRules, values);
  const validated = runValidation(validationObject);

  return mergeAll(validated) as TValidationErrors<T>;
};

const createInitialErrorState = <T>(initialState: T): TValidationErrors<T> =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapObjIndexed(() => [])(initialState) as any;

/**
 *
 * useFormValidation hook enables ease of use form validation to any given input field.
 * Initialize hook with validationRules and initialState, and hook handleChange into input fields
 * onChange listener. Now on every change validationRules for that specific field are run.
 *
 * @param {TValidationRules<T>} validationRules
 * @param {T} initialState
 * @returns {Object} useFormValidation
 * @returns {boolean} useFormValidation.isTouched Has any of the fields been visited
 * @returns {TValidationErrors} useFormValidation.errors Errors {key: string[]}
 * @returns {(event: BaseSyntheticEvent) => void} useFormValidation.handleChange onChange input field listener
 * @returns {T} useFormValidation.values changed values
 */
const useFormValidation = <T>(
  validationRules: TValidationRules<T>,
  initialState: T
) => {
  const [touched, setTouched] = useState<string[]>([]);
  const [errors, setErrors] = useState<TValidationErrors<T>>(
    createInitialErrorState<T>(initialState)
  );
  const [isValid, setIsValid] = useState(true);
  const [values, setValues] = useState(initialState);

  useEffect(() => {
    setErrors(runValidationRules(validationRules, values, touched));
  }, [values, validationRules, touched]);

  useEffect(() => {
    const errorCount = Object.values(errors)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((value: any) => {
        return value.length;
      })
      .reduce((acc, val) => acc + val, 0);
    setIsValid(errorCount === 0);
  }, [errors]);

  const handleChange = useCallback(
    (event: BaseSyntheticEvent) => {
      event.persist();
      setTouched(uniq([...touched, event.target.name]));
      setValues(assocPath([event.target.name], event.target.value, values));
    },
    [setValues, values, setTouched, touched]
  );

  return {
    isTouched: !!touched.length,
    isValid,
    handleChange,
    errors,
    values,
  };
};

export { useFormValidation };
